import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { parseQueryToSearchObject } from "./useSearchFlow";

const isHandlingPopRef = { current: false };

export const popState = {
  get isInPopState() {
    return isHandlingPopRef.current;
  }
};

/**
 * Sync form state from the URL on browser back/forward.
 * - Skip the first POP via `skipInitial`.
 * - Ensure exactly one run per POP using `location.key` (avoid duplicates).
 * - `isHandlingPopRef` is set so submit() won’t navigate.
 * - Rebuild draft from query: parseQueryToSearchObject(location.search) → replaceDraft.
 * - Restore selected filters from history.state.selectedFilters (empty array clears).
 * - Invoke `onNavigate()` to fetch using committed state without pushing history
 *   (e.g. submit({ keepPage: true, skipNavigate: true })).
 * - Clear the POP flag in a microtask.
 */
export function useSyncFromUrlOnPop({
  replaceDraft, 
  setSelectedFilterKeys,
  onNavigate,              
  skipInitial = true,
}: {
  replaceDraft: (o: any) => void;
  setSelectedFilterKeys: (o: any) => void;
  onNavigate?: () => void;
  skipInitial?: boolean;
}) {
  const location = useLocation();
  const navType = useNavigationType();
  const first = useRef(true);
  const handledKeyRef = useRef<string | null>(null);
  const onNavigateRef = useRef(onNavigate);
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  useEffect(() => {
    if (navType !== "POP") {
      handledKeyRef.current = null;
      return;
    }

    const isInitial = first.current;
    if (first.current) first.current = false;
    if (isInitial && skipInitial) {
      handledKeyRef.current = location.key;
      return;
    }
    if (handledKeyRef.current === location.key) return;
    handledKeyRef.current = location.key;

    isHandlingPopRef.current = true;

    const fromQuery = parseQueryToSearchObject(location.search);
    const selectedKeys = (location.state as any)?.selectedFilters as string[] | undefined;

    replaceDraft(fromQuery);
    setSelectedFilterKeys(selectedKeys || []);
    onNavigateRef.current && onNavigateRef.current();

    queueMicrotask(() => { isHandlingPopRef.current = false; });
  }, [location.key, location.search, location.state, navType, replaceDraft, setSelectedFilterKeys, skipInitial]);
}
